import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { throttle } from "lodash";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { actionCreatorAuth } from "../../auth/auth.reducer";
import { searchUrl } from "../../common/infrastructure/endpoints";
import { get } from "../../common/infrastructure/requests";
import { IApplicationState } from "../../redux/root-reducer";
import SearchResult from "./SearchResult";
import { INode } from "../types";

interface ISearchNodeProps {
  onChange: (node: INode | string | null) => void;
  node: INode | undefined;
}

const getDomainSelector = createSelector(
  (state: IApplicationState) => state.common,
  (commonState) => ({
    domainCode: commonState.selectedDomain?.Code ?? "",
  })
);

const SearchNode: React.FC<ISearchNodeProps> = (props) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [options, setOptions] = React.useState<INode[]>([]);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const { domainCode } = useSelector(getDomainSelector);
  const dispatch = useDispatch();
  let input: any = useRef();

  const goSearch = React.useMemo(
    () =>
      throttle(
        async (
          searchTerm: { input: string; code: string },
          callback: (response: INode[]) => void
        ) => {
          try {
            const { input, code } = searchTerm;
            setLoading(true);
            const response = await get<INode[]>(`${searchUrl(code, input)}`);
            callback(response as INode[]);
            setLoading(false);
          } catch (e) {
            if (
              (e as Error).message === "401" ||
              (e as Error).message === "403"
            ) {
              dispatch(actionCreatorAuth.unauthorized);
            } else {
              throw e;
            }
          }
        },
        300
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions([]);
      return undefined;
    }

    goSearch({ input: inputValue, code: domainCode }, (results: INode[]) => {
      if (active) {
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, goSearch]);

  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      if (options.length > 0) {
        props.onChange(options[0]);
      } else {
        //@ts-ignore
        props.onChange(undefined);
      }
    }
  };

  const onSearch = (searchTerm: string) => {
    setInputValue(searchTerm);
  };
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Autocomplete
        id="asynchronous-demo"
        autoHighlight
        freeSolo
        filterOptions={(x) => x}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(e, value: INode | string | null) =>
          value && props.onChange(value)
        }
        getOptionLabel={(option: INode | null) =>
          typeof option === "string"
            ? option
            : `${option?.Name} (${option?.ObjectNumber})`
        }
        options={options}
        loading={loading}
        renderOption={(option: INode) => (
          <React.Fragment>
            <SearchResult result={option} />
          </React.Fragment>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Enter object number")}
            fullWidth
            onKeyDown={onKeyDown}
            onChange={(e) => onSearch(e.target.value)}
            variant="outlined"
            inputRef={input}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </React.Fragment>
  );
};

export default SearchNode;
